import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { ImpersonationBroker, type IPagedCollection } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import type { IFormFieldOption } from '../../viewModels/form';

/** One choice for an Option / MultipleOptions lead attribute. */
export type LeadAttributeOption = {
	id: string;
	label: string;
	/** Option color, when the CRM property defines one. */
	hexColor?: string;
};

export type UseLeadAttributeOptionsResult = {
	options: LeadAttributeOption[];
	/** True while any fetch is in flight, including a re-fetch for a new `search` term. */
	isFetching: boolean;
	error: Error | null;
};

export function useLeadAttributeOptions(
	companyId: string | undefined,
	fieldId: string | undefined,
	open: boolean,
	search = ''
): UseLeadAttributeOptionsResult {
	const userSession = useUserSession();

	const query = useQuery<LeadAttributeOption[], Error>({
		queryKey: ['focused-view', 'lead-attributes', 'field-options', companyId, fieldId, search] as const,
		enabled: !!companyId && !!fieldId && open,
		queryFn: async () => {
			const response = await userSession.webServiceHelper.callAsync<IPagedCollection<IFormFieldOption>>(
				ImpersonationBroker.composeApiUrl({
					// The endpoint pages at 25, so unsearched callers only ever see the first page —
					// `search` is the only way to reach an option beyond it.
					queryParams: { search },
					urlPath: `lead/${companyId}/Lead/Field/${fieldId}`,
				}),
				'GET'
			);
			return (response.values ?? []).map(option => ({
				id: option.id ?? '',
				label: option.label ?? '',
				hexColor: option.hexColor,
			}));
		},
		placeholderData: keepPreviousData,
		// Toast fetch failures — otherwise a failed options fetch silently renders "No options" as if the field had none.
		meta: { toastErrorMessage: 'Failed to load field options' },
	});

	return {
		options: query.data ?? [],
		isFetching: query.isFetching,
		error: query.error ?? null,
	};
}
